import React, { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Checkbox } from '@material-ui/core'
import AdminPage from '../../../shared/AdminPage'
import { useDetailStyles } from '../../TagDetails'
import SydButton from '../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SaveCancelFooter from '../../../shared/SaveCancelFooter'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydTextArea from '../../../../../commonDesign/SydTextArea'
import { useGetDatapointDetails, useModifyDatapointDetails } from '../../../../../../api/groups'
import Loading from '../../../../../molecules/Loading'
import KdpListFields from './kdpListFields'
const types = [
  'text',
  'boolean',
  'number',
  'string',
  'date',
  'integer',
  'text area',
  'list'
]
const levelMap = {
  1: 'Account',
  201: 'Client',
  3: 'Product'
}
const KeyDatapointsDetails = () => {
  const params = useParams()
  const { data, isLoading } = useGetDatapointDetails(params.kdpCode)
  const { mutateAsync, isLoading: saveLoading, error } = useModifyDatapointDetails()
  const history = useHistory()
  const classes = useDetailStyles()
  const [editing, setEditing] = React.useState(params.kdpCode === 'new')
  const [jsonType, setJsonType] = React.useState('date')
  const toggleEditing = () => {
    setEditing(!editing)
  }
  const form = useForm({
    mode: 'onChange',
    defaultValues:
      {
        codeName: params?.kdpCode === 'new' ? '' : params.kdpCode,
        displayName: data?.displayName || '',
        automated: data?.automated === 1,
        readonly: data?.readOnly === 1,
        type: data?.configurationJson?.type || 'text',
        description: data?.configurationJson?.description || '',
        options: data?.configurationJson?.options || [{ label: '', value: '' }],
        levelTypeId: data?.levelTypeId || 201
      }
  })
  const onSubmit = async (data) => {
    const configuration = {
      type: data.type
    }
    if (data.type === 'list') {
      if (data.options && data.options.length > 0) configuration.options = data.options
    }
    configuration.description = data.description
    const kdpData = {
      ...data,
      isNew: params.kdpCode === 'new',
      configuration: configuration
    }
    const result = await mutateAsync(kdpData)
    if (result.success) {
      setEditing(false)
      if (params.kdpCode === 'new') history.push(`/admin/data-maintenance/key-data-points/${result.data.codeName}`)
    }
  }
  useEffect(() => {
    if (error?.message?.includes('Duplicate Entry')) {
      form.setError('codeName', { type: 'manual', message: 'This code name is already in use and must be unique.' })
    }
  }, [error, form])

  useEffect(() => {
    setJsonType(data?.configurationJson?.type)
    form.reset({
      codeName: params.kdpCode === 'new' ? '' : params.kdpCode,
      displayName: data?.displayName || '',
      automated: data?.automated === 1,
      readonly: data?.readOnly === 1,
      type: data?.configurationJson?.type || 'text',
      description: data?.configurationJson?.description || '',
      options: data?.configurationJson?.options || [{ label: '', value: '' }],
      levelTypeId: data?.levelTypeId || 201
    })
  }, [form, params?.kdpCode, data])
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'options'
  })
  if (isLoading) {
    return <Loading />
  }
  return (
    <AdminPage>
      <h1 className='text-2xl font-bold mb-0'>
        {data?.displayName || 'New KDP'}
      </h1>
      <hr />
      <div>
        <form className='flex flex-col gap-2' onSubmit={form.handleSubmit(onSubmit)}>
          <div className={classes.detailsHeader}>
            <h1>KDP Information</h1>
            {params.kdpCode !== 'new' && <SydButton size='sm' icon='edit' onClick={toggleEditing}>Edit</SydButton>}
          </div>
          <div className={classes.detailRow}>
            <div>
              <Controller
                control={form.control}
                name='displayName'
                rules={{ required: true }}
                render={(f) => (
                  <SydLabel label='Display Name' required error={hookFormErrorAdapter(form, f.fieldState)}>
                    <SydInput disabled={!editing} {...f.field} />
                  </SydLabel>
                )}
              />
            </div>
            <div>
              <Controller
                control={form.control}
                rules={{ required: true }}
                name='codeName'
                render={(f) => (
                  <SydLabel label='Code Name' required error={hookFormErrorAdapter(form, f.fieldState)}>
                    <SydInput disabled={params.kdpCode !== 'new'} {...f.field} />
                  </SydLabel>
                )}
              />
            </div>
          </div>
          <div>
            <div className={classes.detailRow}>
              <div className={classes.customRowContainer}>
                <Controller
                  control={form.control}
                  name='automated'
                  render={(f) => (
                    <SydLabel label='Automated'>
                      <Checkbox
                        label='Automated'
                        disabled={!editing}
                        checked={f.field.value ?? false}
                        onChange={f.field.onChange}
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  control={form.control}
                  name='readonly'
                  render={(f) => (
                    <SydLabel label='Read Only'>
                      <Checkbox
                        label='Read Only'
                        disabled={!editing}
                        checked={f.field.value ?? false}
                        onChange={f.field.onChange}
                      />
                    </SydLabel>
                  )}
                />
              </div>
              <div>
                <Controller
                  control={form.control}
                  name='levelTypeId'
                  render={(f) => (
                    <SydLabel label='Level'>
                      <SydSelect
                        options={Object.entries(levelMap).map(([id, label]) => ({
                          label,
                          value: Number(id)
                        }))}
                        value={f.field.value}
                        onChange={f.field.onChange}
                        disabled={params.kdpCode !== 'new'}
                        style={{ width: '300px', overflow: 'hidden' }}
                      />
                    </SydLabel>
                  )}
                />
              </div>
            </div>
          </div>
          <div className={jsonType === 'list' ? classes.kvpFields : classes.detailStartRow}>
            <Controller
              control={form.control}
              name='type'
              rules={{ required: true }}
              render={(f) => (
                <SydLabel label='Type' required>
                  <div className='flex flex-row gap-0'>
                    <SydSelect
                      disabled={!editing}
                      onChange={(e) => {
                        f.field.onChange(e.target.value)
                        setJsonType(e.target.value)
                      }}
                      value={f.field.value ?? jsonType}
                      options={types}
                      defaultValue={types[0]}
                      style={{ width: '300px', overflow: 'hidden' }}
                    />
                  </div>
                </SydLabel>
              )}
            />
            <div className={classes.detailStartCol}>
              {
                jsonType === 'text area' ? (
                  <Controller
                    control={form.control}
                    name='description'
                    render={(f) => (
                      <SydLabel label='Description'>
                        <SydTextArea disabled={!editing} {...f.field} />
                      </SydLabel>
                    )}
                  />
                ) : (
                  <Controller
                    control={form.control}
                    name='description'
                    render={(f) => (
                      <SydLabel label='Description'>
                        <SydInput disabled={!editing} {...f.field} size='lg' />
                      </SydLabel>
                    )}
                  />
                )
              }
              {jsonType === 'list' && (
                <KdpListFields
                  fields={fields}
                  form={form}
                  append={append}
                  remove={remove}
                  editing={editing}
                />
              )}
            </div>
          </div>
          {editing &&
            <SaveCancelFooter
              onSave={form.handleSubmit(onSubmit)}
              processing={saveLoading}
              onCancel={() => {
                if (params.kdpCode === 'new') {
                  history.push('/admin/data-maintenance/key-data-points')
                }
                form.reset()
                setEditing(false)
              }}
            />}
        </form>
      </div>
    </AdminPage>
  )
}

export default KeyDatapointsDetails
